import type { MarkdownParserPort } from '@/domain/markdown/MarkdownParserPort'
import type { Granularity, ParsedDocument } from '@/domain/markdown/types'

/**
 * Upload-document use case (PRD #11, #58).
 *
 * Validates the file is a markdown file by extension and that it does
 * not exceed the size cap, then asks the parser to produce a
 * ParsedDocument. Slice 1.0 keeps the result purely in-memory; Slice
 * 1.2 introduces the project repository and persists.
 */
export const MAX_BYTES = 5 * 1024 * 1024 // 5 MiB

export type UploadDocumentErrorCode =
  | 'invalid_extension'
  | 'oversize'
  | 'empty'
  | 'unreadable'

export class UploadDocumentError extends Error {
  readonly code: UploadDocumentErrorCode

  constructor(code: UploadDocumentErrorCode, message: string) {
    super(message)
    this.code = code
    this.name = 'UploadDocumentError'
  }
}

const ALLOWED_EXTENSIONS = ['.md', '.markdown'] as const

function hasMarkdownExtension(name: string): boolean {
  const lower = name.toLowerCase()
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext))
}

export async function uploadDocument(
  file: File,
  parser: MarkdownParserPort,
  granularity: Granularity = 'section',
): Promise<ParsedDocument> {
  if (!hasMarkdownExtension(file.name)) {
    throw new UploadDocumentError(
      'invalid_extension',
      `Only .md or .markdown files are accepted (got "${file.name}").`,
    )
  }

  if (file.size > MAX_BYTES) {
    throw new UploadDocumentError(
      'oversize',
      `File is ${(file.size / 1024 / 1024).toFixed(1)} MiB; the limit is ${MAX_BYTES / 1024 / 1024} MiB.`,
    )
  }

  if (file.size === 0) {
    throw new UploadDocumentError('empty', 'The file is empty.')
  }

  let text: string
  try {
    text = await file.text()
  } catch (cause) {
    throw new UploadDocumentError(
      'unreadable',
      `Could not read the file: ${cause instanceof Error ? cause.message : String(cause)}`,
    )
  }

  return await parser.parse(text, granularity)
}
